import { useCallback, useEffect } from 'react';

import { useBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { FastshiftExpectedError } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import Breadcrumbs from '@atlaskit/breadcrumbs/breadcrumbs';
import { BreadcrumbsItem } from '@atlaskit/breadcrumbs/breadcrumbs-item';
import { BreadcrumbsSkeleton } from '@atlaskit/breadcrumbs/breadcrumbs-skeleton';
import { BreadcrumbsSkeletonItem } from '@atlaskit/breadcrumbs/breadcrumbs-skeleton-item';
import Heading from '@atlaskit/heading';
import { Stack } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';

import { FourOhFourDisplay } from '@/common/displays';
import { GenericErrorDisplay } from '@/common/displays/src/generic-error-display/generic-error-display';
import { useRoutes } from '@/common/hooks';
import { useFastshiftDispatch, useFastshiftSelector } from '@/state';
import { getFastshiftDcInstanceDetails } from '@/state/actions';

import { dcInstanceDetailsMessages } from './dc-instance-details-messages';

export const FastshiftDcInstanceDetailsPage = () => {
	const { formatMessage } = useIntl();
	const { paths } = useRoutes();
	const stargateClient = useBxpWebStargateClient();
	const dispatch = useFastshiftDispatch();
	const { domainId, organizationId, instanceId } = useParams<{
		domainId: string;
		organizationId: string;
		instanceId: string;
	}>();
	const { pending, error, data } = useFastshiftSelector((state) => state.dcInstanceDetail);

	const loadDetails = useCallback(() => {
		if (!domainId || !organizationId || !instanceId) {
			return;
		}

		dispatch(
			getFastshiftDcInstanceDetails({
				stargateClient,
				domainId,
				orgId: organizationId,
				instanceId,
			}),
		);
	}, [dispatch, domainId, instanceId, organizationId, stargateClient]);

	useEffect(() => {
		loadDetails();
	}, [loadDetails]);

	if (!domainId || !organizationId || !instanceId) {
		return <FourOhFourDisplay />;
	}

	if (!pending) {
		if (error === FastshiftExpectedError.NOT_FOUND) {
			return <FourOhFourDisplay />;
		}

		if (error || !data) {
			return <GenericErrorDisplay />;
		}
	}

	const pageTitle = data?.baseUrl ?? formatMessage(dcInstanceDetailsMessages.pageTitleFallback);

	return (
		<Stack space="space.300">
			{pending ? (
				<BreadcrumbsSkeleton
					label={formatMessage(dcInstanceDetailsMessages.loadingLabel)}
					testId="dc-instance-details-breadcrumb-skeleton"
				>
					<BreadcrumbsSkeletonItem width={100} />
					<BreadcrumbsSkeletonItem width={120} />
					<BreadcrumbsSkeletonItem width={160} />
				</BreadcrumbsSkeleton>
			) : (
				<Breadcrumbs>
					<BreadcrumbsItem
						href={paths.domainScopedOrganizations}
						text={formatMessage(dcInstanceDetailsMessages.organizationsBreadcrumb)}
					/>
					<BreadcrumbsItem
						href={paths.domainScopedOrganizationDetails}
						text={formatMessage(dcInstanceDetailsMessages.organizationBreadcrumb)}
					/>
					<BreadcrumbsItem href={paths.domainScopedDcInstanceDetails} text={pageTitle} />
				</Breadcrumbs>
			)}
			{pending ? (
				<Skeleton
					height="32px"
					width="300px"
					isShimmering
					testId="dc-instance-details-heading-skeleton"
				/>
			) : (
				<Heading size="large" as="h1">
					{pageTitle}
				</Heading>
			)}
		</Stack>
	);
};
